import React, { Component, createRef, RefObject } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { EarningsPieLegend } from '..';

/**
 * @typedef {EarningsPerShowProps}
 * @property {string} chartDivId a unique ID for this chart instance
 * @property {number} height height for the chart div
 * @property {ProShow[]} shows array of ProShow elements to render
 */
interface EarningsPerShowProps {
    chartDivId: string;
    height: number;
    shows: ProShow[];
}

interface EarningsPerShowState {
    chart: am4charts.PieChart | null;
    pieSeries: am4charts.PieSeries | null;
}

const hideSmall = (hidden: boolean, target: Record<string, unknown>): boolean =>
    _get(target, ['dataItem', 'values', 'value', 'percent'], 0) < 0.5;

export class Chart extends Component<
    EarningsPerShowProps,
    EarningsPerShowState
> {
    // There is a race condition in amCharts which can cause issues if we use a chart div ID rather
    // than a ref. The chartDivId prop is only used as a fallback for type safety; the ref
    // should be available by the time componentDidMount is called.
    private divRef: RefObject<HTMLDivElement> | null = null;

    constructor(props: EarningsPerShowProps) {
        super(props);

        this.divRef = createRef();
        this.state = {
            chart: null,
            pieSeries: null,
        };
    }

    componentDidMount(): void {
        am4core.createDeferred((): am4core.Sprite => {
            const { chartDivId, shows } = this.props;

            const chart = am4core.create(
                this.divRef?.current ?? chartDivId,
                am4charts.PieChart,
            );

            const pieSeries = chart.series.push(new am4charts.PieSeries());

            chart.data = shows;
            chart.innerRadius = am4core.percent(40);
            chart.numberFormatter.numberFormat = '#.00a';
            chart.numberFormatter.bigNumberPrefixes = [
                { number: 1e3, suffix: 'K' },
                { number: 1e6, suffix: 'M' },
                { number: 1e9, suffix: 'B' },
                { number: 1e12, suffix: 'T' },
                { number: 1e15, suffix: 'Q' },
            ];
            chart.numberFormatter.smallNumberThreshold = 1e-12;
            chart.numberFormatter.smallNumberPrefixes = [
                { number: 1e-12, suffix: '' },
            ];

            pieSeries.dataFields.value = 'earnings';
            pieSeries.dataFields.category = 'show_name';
            pieSeries.calculatePercent = true;
            pieSeries.slices.template.tooltipText =
                // eslint-disable-next-line no-template-curly-in-string
                '[bold]{category}[/]: ${value.value} ({value.percent}%)';
            pieSeries.slices.template.stroke = am4core.color('#fff');
            pieSeries.slices.template.strokeWidth = 1;
            pieSeries.slices.template.strokeOpacity = 1;
            // eslint-disable-next-line
            // @ts-ignore
            pieSeries.ticks.template.adapter.add('hidden', hideSmall);
            // pieSeries.labels.template.adapter.add('hidden', hideSmall);
            pieSeries.labels.template.disabled = true;

            // This creates initial animation
            pieSeries.hiddenState.properties.opacity = 1;
            pieSeries.hiddenState.properties.endAngle = -90;
            pieSeries.hiddenState.properties.startAngle = -90;

            this.setState({ chart, pieSeries });

            return chart;
        }, this);
    }

    componentDidUpdate(prevProps: EarningsPerShowProps): void {
        const { chart } = this.state;
        const { shows } = this.props;

        if (!_isEqual(prevProps.shows, shows) && chart !== null) {
            chart.data = shows;
        }
    }

    componentWillUnmount(): void {
        const { chart } = this.state;

        if (chart) {
            chart.dispose();
        }
    }

    render(): JSX.Element {
        const { chartDivId, height } = this.props;
        const { chart, pieSeries } = this.state;

        return (
            <div className="flex text-sm">
                <div
                    id={chartDivId}
                    data-cy="earnings-per-show-chart"
                    ref={this.divRef}
                    className="w-2/3"
                    style={{ height }}
                />
                <EarningsPieLegend
                    addClasses="w-1/3 ml-3"
                    height={height}
                    chart={chart}
                    pieSeries={pieSeries}
                />
            </div>
        );
    }
}
